// CalendarComponent.jsx

import React, { useContext, useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import './Calendar.css';
import { TaskContext } from './TaskContext';
import { useNightMode } from '../NightModeContext';
import EditEventModal from './EditEventModal'; // Import the modal component
import { Timestamp } from "firebase/firestore";

const CalendarComponent = ({ filterGroupIds }) => {
  const { events, toggleCompletion, editEvent, deleteEvent, groups } = useContext(TaskContext); // Assuming TaskContext also provides events
  const { isNightMode } = useNightMode();
  const calendarRef = useRef(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);

  const calculateMaxHeightBasedOnDuration = (duration) => {
    const minutes = duration / 60000; // Convert duration from milliseconds to minutes
    const heightPerMinute = 2; // Determine how many pixels per minute you want
    return minutes * heightPerMinute; // This gives you a dynamic max height based on the event duration
  };

  const filteredEvents = events.filter(event => 
    filterGroupIds.length === 0 || filterGroupIds.includes(event.groupId)
  );

  const handleEventClick = ({ event }) => {
    console.log("Event clicked for editing:", event); // Check if this logs correctly when you click an event

    setEventToEdit({
      id: event.id,
      title: event.title,
      start: event.start instanceof Timestamp ? event.start.toDate() : event.start,
      end: event.end instanceof Timestamp ? event.end.toDate() : event.end,
      color: event.backgroundColor,  // Add this if color is editable
    });

    setEditModalOpen(true); // Make sure this is set to open the modal
  };




  const saveEvent = (updatedEvent) => {
    if (updatedEvent && updatedEvent.id) {

      const groupColor = groups.find(group => group.id === updatedEvent.groupId)?.color;

      const updates = {
        title: updatedEvent.title,
        start: updatedEvent.start instanceof Date ? Timestamp.fromDate(updatedEvent.start) : updatedEvent.start,
        end: updatedEvent.end instanceof Date ? Timestamp.fromDate(updatedEvent.end) : updatedEvent.end,
        // Ensure all other required fields are here
        groupId: updatedEvent.groupId,
        color: groupColor, 
      };

      console.log("Updates to save:", updates); // Debugging line to check updates object

      if (Object.values(updates).some(value => value === undefined)) {
        console.error("Some properties are undefined:", updates);
        return; // Stop the execution if any property is undefined
      }

      editEvent(updatedEvent.id, updates);
    } else {
      console.error("Updated event is undefined or missing ID");
    }
  };




  const transformedFilteredEvents = filteredEvents.map((event) => {
    return {
      ...event,
      start: event.start.toDate(), // Convert Timestamp to Date
      end: event.end.toDate(),     // Convert Timestamp to Date
      backgroundColor: event.color,
      completed: event.completed ?? false,
      // ...other event properties if needed
    };
  });

  useEffect(() => {
    const calendarEl = document.querySelector('.fc');
    if (calendarEl) {
      if (isNightMode) {
        calendarEl.classList.add('night-mode');
      } else {
        calendarEl.classList.remove('night-mode');
      }
    }
  }, [isNightMode]);

  // Function to render the content of each event with group-based coloring
  const eventContent = (eventInfo) => {
    const eventDuration = eventInfo.event.end - eventInfo.event.start;
    const contentMaxHeight = calculateMaxHeightBasedOnDuration(eventDuration); // Make sure this function returns a valid number.

    // Apply the background color from the event's properties
    const eventStyles = {
      backgroundColor: eventInfo.event.backgroundColor,
      color: '#fff', // Set text color to white for better contrast
      borderRadius: '4px', // Optional: if you want rounded corners
      //padding: '2px 5px',
      padding: '5px', // Adjust padding as necessary
      margin: '0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    };

    const contentStyles = {
      overflowY: 'auto', // Scrollable content
      maxHeight: `${contentMaxHeight}px`, // Max height for inner content, not the event container
    };
  



    return (
      <div className="fc-event-outer" style={eventStyles}>
        <div className="fc-event-content" style={contentStyles}>
          {/* Scrollable container for the inner content */}
          <div className="fc-event-content-inner">
            {/* Place all content here, including checkboxes, titles, and buttons */}
            <div className="checkbox-container">
              <input
                type="checkbox"
                checked={eventInfo.event.extendedProps.completed}
                onChange={() => toggleCompletion(eventInfo.event.id)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <span style={{ textDecoration: eventInfo.event.extendedProps.completed ? 'line-through' : 'none' }}>
              {eventInfo.timeText} - {eventInfo.event.title}
            </span>
            {eventInfo.view.type !== 'dayGridMonth' && (
              <>
                <button onClick={() => editEvent(eventInfo.event.id)} style={{ marginLeft: '4px' }}>Edit</button>
                <button onClick={(e) => { e.stopPropagation(); deleteEvent(eventInfo.event.id); }} style={{ marginLeft: '4px' }}>Delete</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="calendar-scroll-container">
      <div className="calendar-container">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          slotDuration={'00:30:00'}
          slotLabelInterval={'00:30:00'}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short',
            hour12: true
          }}
          allDaySlot={false}
          expandRows={true}
          height="auto"
          events={transformedFilteredEvents} // Use transformed events here
          eventContent={eventContent} // // Custom event rendering function
          eventClick={handleEventClick}
          themeSystem={isNightMode ? 'bootstrap' : 'standard'}
        />
        <EditEventModal
          isOpen={editModalOpen}
          eventToEdit={eventToEdit}
          onClose={() => setEditModalOpen(false)}
          onSave={saveEvent}
          groups={groups}
        />
      </div>
    </div>
  );
};

export default CalendarComponent;
